import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppBar, Toolbar, Box, Avatar, IconButton, Badge, useMediaQuery, useTheme } from '@mui/material';
import { Tooltip as MuiTooltip } from '@mui/material';
import { MailOutline } from '@mui/icons-material';
import BrandLogo from './BrandLogo';
import NotificationBell from './NotificationBell';
import { useNotifications } from '../hooks/useNotifications';
import { useDirectMessages } from '../hooks/useDirectMessages';
import { authService } from '../services/authService';
import { buddyService } from '../services/buddyService';

function initials(name: string) {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(w => w[0].toUpperCase())
    .join('');
}

const Header: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isDark = theme.palette.mode === 'dark';

  const currentUser = authService.getUser();
  const userId = currentUser?.userId ?? null;
  const displayName = currentUser ? `${currentUser.firstName} ${currentUser.lastName}`.trim() : '';
  const { notifications, unreadCount, markRead, markAllRead, dismiss } = useNotifications(userId);
  const { unreadCount: unreadMessageCount } = useDirectMessages(userId);

  // Only the profile page's own fetch normally knows about the photo — the header
  // needs its own copy since it renders on every page, not just /u/:userId.
  const [profilePhotoUrl, setProfilePhotoUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!userId) {
      setProfilePhotoUrl(null);
      return;
    }
    buddyService.getUserProfile(userId)
      .then(profile => setProfilePhotoUrl(profile.profilePhotoUrl ?? null))
      .catch(() => setProfilePhotoUrl(null));
  }, [userId]);

  useEffect(() => {
    // ProfileEditTab dispatches this right after a successful upload so the
    // header's avatar updates immediately, without waiting for a reload.
    const handlePhotoUpdated = (e: Event) => {
      setProfilePhotoUrl((e as CustomEvent<{ photoUrl: string }>).detail.photoUrl);
    };
    window.addEventListener('profilePhotoUpdated', handlePhotoUpdated);
    return () => window.removeEventListener('profilePhotoUpdated', handlePhotoUpdated);
  }, []);

  const textColor = isDark ? theme.palette.text.primary : '#FFFFFF';
  const accentColor = isDark ? theme.palette.primary.light : theme.palette.secondary.light;

  return (
    <AppBar position="static">
      <Toolbar sx={{ minHeight: { xs: 72, sm: 84 }, px: { xs: 1.5, sm: 2 } }}>
        <Box
          onClick={() => navigate('/')}
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            minWidth: 0,
            '&:hover': { opacity: 0.88 },
          }}
        >
          <BrandLogo
            size={isMobile ? 44 : 58}
            textColor={textColor}
            accentColor={accentColor}
            subtitle={isMobile ? undefined : 'Where divers connect'}
          />
        </Box>

        <Box sx={{ ml: 'auto' }} />

        {userId && (
          <MuiTooltip title="Messages">
            <IconButton aria-label="Messages" onClick={() => navigate('/messages')} sx={{ color: textColor }}>
              <Badge badgeContent={unreadMessageCount} color="error" max={9}>
                <MailOutline />
              </Badge>
            </IconButton>
          </MuiTooltip>
        )}

        <NotificationBell
          notifications={notifications}
          unreadCount={unreadCount}
          onMarkRead={markRead}
          onMarkAllRead={markAllRead}
          onDismiss={dismiss}
        />

        {userId && (
          <MuiTooltip title="My Profile">
            <IconButton
              aria-label="My Profile"
              onClick={() => navigate(`/u/${userId}`)}
              sx={{ ml: 0.5 }}
            >
              <Avatar
                src={profilePhotoUrl ?? undefined}
                sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: '0.8rem', fontWeight: 700 }}
              >
                {!profilePhotoUrl && (initials(displayName) || '?')}
              </Avatar>
            </IconButton>
          </MuiTooltip>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default Header;
